import { useMutation } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { cn } from '../../shared/lib/cn';
import { calendarDay, clockTime, spanText } from '../../shared/lib/format';
import { Button } from '../../shared/ui/button';
import { DoneIcon, FailedIcon } from '../../shared/ui/icons';
import { MutationError } from '../../shared/ui/mutation-error';
import { Note } from '../../shared/ui/note';
import { SectionPanel } from '../../shared/ui/section-panel';
import {
  formatBytes,
  ownerName,
  ownersText,
  periodLabel,
  periodOfDays,
  retryCleanup,
  useRefreshStorage,
  type TRetentionUsage,
  type TStorageCleanup,
  type TStorageLevels,
} from '../../entities/retention';

const SHOWN = 4;
const UTC = 'UTC';

const flowsOf = (owners: TStorageCleanup['owners']) =>
  owners.filter((owner) => owner.kind === 'flow');
const Line = ({ label, value }: { label: ReactNode; value: ReactNode }) => (
  <div className="flex justify-between gap-3 py-1 text-meta leading-18">
    <span className="min-w-0 truncate text-muted">{label}</span>
    <span className="shrink-0 text-right font-mono text-primary">{value}</span>
  </div>
);

const Mark = ({ children, className }: { children: ReactNode; className?: string }) => (
  <p className={cn('font-mono text-micro text-faint', className)}>{children}</p>
);

const dayWords = (iso: string, now: number): string =>
  calendarDay(iso, now, UTC) === calendarDay(new Date(now).toISOString(), now, UTC)
    ? `today at ${clockTime(iso, UTC)}`
    : `${calendarDay(iso, now, UTC)} at ${clockTime(iso, UTC)}`;

const LastRun = ({ run, now }: { run: TStorageCleanup; now: number }) => {
  const took = run.finishedAt ? Date.parse(run.finishedAt) - Date.parse(run.startedAt) : 0;
  return (
    <div className="mt-3 border-t border-border-sunken pt-3">
      <span className="inline-flex items-center gap-label text-meta text-primary">
        <DoneIcon size="sm" className="text-success" />
        Last run {dayWords(run.startedAt, now)}
      </span>
      <Mark className="mt-label">
        {run.removedBytes > 0
          ? `removed ${formatBytes(run.removedBytes)} from ${ownersText(run.owners)} in ${spanText(took)}`
          : `nothing was due · took ${spanText(took)}`}
      </Mark>
    </div>
  );
};

const Failed = ({ run, usage }: { run: TStorageCleanup; usage: TRetentionUsage }) => {
  const refresh = useRefreshStorage();
  const retry = useMutation({ mutationFn: retryCleanup, onSuccess: refresh });
  const closed = new Date(Date.parse(run.startedAt) + usage.settings.windowMinutes * 60_000);
  const next = usage.settings.nextRunAt ? clockTime(usage.settings.nextRunAt, UTC) : 'tonight';
  const rest = usage.nextCleanup.bytes;
  return (
    <div>
      <span className="inline-flex items-center gap-label text-data text-danger-text">
        <FailedIcon size="sm" />
        Ran out of time at {clockTime(closed.toISOString(), UTC)}
      </span>
      <p className="mt-2 text-meta leading-18 text-primary">
        {run.removedBytes > 0
          ? `It removed ${formatBytes(run.removedBytes)} of ~${formatBytes(run.removedBytes + rest)} before its window closed. `
          : 'Its window closed before it reached this organization. '}
        {rest > 0
          ? `It continues tonight at ${next} with ${run.removedBytes > 0 ? 'the other ' : ''}~${formatBytes(rest)}. `
          : `It continues tonight at ${next} with whatever is left. `}
        Nothing else is affected.
      </p>
      <div className="mt-3 flex items-center gap-3">
        <Button
          variant="secondary"
          size="sm"
          disabled={!usage.settings.allowedByInstallation}
          waiting={retry.isPending}
          waitingLabel="Running"
          onClick={() => retry.mutate()}
        >
          Retry now
        </Button>
        <Mark>or wait for tonight</Mark>
      </div>
      <MutationError error={retry.error} />
    </div>
  );
};

interface INextCleanupCardProps {
  usage: TRetentionUsage;
  levels: TStorageLevels | undefined;
  now: number;
  onBiggest: () => void;
  onDeleteNow: () => void;
}

export const NextCleanupCard = ({
  usage,
  levels,
  now,
  onBiggest,
  onDeleteNow,
}: INextCleanupCardProps) => {
  const at = usage.settings.nextRunAt ? clockTime(usage.settings.nextRunAt, UTC) : '03:30';
  const next = usage.nextCleanup;
  const nextFlows = flowsOf(next.owners);
  const run = usage.lastNightly;
  // Older data follows the default, so "everything is kept" needs the default on Forever too.
  const allForever =
    levels !== undefined &&
    levels.foreverCount === levels.flowCount &&
    usage.settings.period === 'forever';
  const fromOff = usage.settings.period === 'forever' && usage.settings.foreverSince !== null;
  const who =
    levels && levels.flowCount > 0
      ? `${levels.flowCount === 1 ? 'Your flow' : `All ${levels.flowCount.toLocaleString('en-US')} flows`}${levels.older.bytes > 0 ? ' and the older data' : ''} keep`
      : 'The organization keeps';

  let body: ReactNode;
  if (run && !run.complete) {
    body = <Failed run={run} usage={usage} />;
  } else if (allForever && usage.totalBytes > 0) {
    body = (
      <div>
        <p className="text-data text-primary">Nothing to remove tonight</p>
        <p className="mt-1 text-meta leading-18 text-muted">
          {who} everything Forever, so storage only grows. To cap it, set a period for the
          organization, a folder or a flow below. To remove old data once, use Delete now.
        </p>
        <div className="mt-3 flex items-center gap-2">
          {levels.flowCount > 0 && (
            <Button variant="secondary" size="sm" onClick={onBiggest}>
              Go to the biggest flows
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onDeleteNow}>
            Delete now
          </Button>
        </div>
        {fromOff && (
          <Note className="mt-3">
            Automatic removal was off here, so when retention moved to folders and flows on{' '}
            {calendarDay(usage.settings.foreverSince!, now)} the default was set to Forever. Nothing
            was deleted.
          </Note>
        )}
      </div>
    );
  } else if (next.bytes === 0) {
    body = (
      <div>
        <p className="text-data text-primary">
          {run ? 'Nothing to remove tonight' : 'Nothing to remove'}
        </p>
        {!run && (
          <p className="mt-1 text-meta leading-18 text-muted">
            Cleanup runs every night; it has had nothing to do yet.
          </p>
        )}
        {run && <LastRun run={run} now={now} />}
      </div>
    );
  } else {
    const older = next.owners.find((owner) => owner.kind === 'older');
    const shown = [...nextFlows.slice(0, SHOWN), ...(older ? [older] : [])];
    const rest = next.bytes - shown.reduce((sum, owner) => sum + owner.bytes, 0);
    const restFlows = nextFlows.length - Math.min(SHOWN, nextFlows.length);
    body = (
      <div>
        <p className="text-meta text-muted">Tonight</p>
        <p className="mt-hair mb-3">
          <span className="text-title font-semibold text-primary">~{formatBytes(next.bytes)}</span>
          <span className="text-data text-muted">
            {' '}
            from{' '}
            {nextFlows.length > 0
              ? `${nextFlows.length.toLocaleString('en-US')} ${nextFlows.length === 1 ? 'flow' : 'flows'}${older ? ' and older data' : ''}`
              : 'older data'}
          </span>
        </p>
        {shown.map((owner) => (
          <Line
            key={`${owner.kind}:${owner.id}`}
            label={`${ownerName(owner)}${
              owner.contentDays
                ? ` · older than ${periodLabel(periodOfDays(owner.contentDays))}`
                : ''
            }`}
            value={formatBytes(owner.bytes)}
          />
        ))}
        {rest > 0 && restFlows > 0 && (
          <Line
            label={`${restFlows.toLocaleString('en-US')} more ${restFlows === 1 ? 'flow' : 'flows'}`}
            value={formatBytes(rest)}
          />
        )}
        {run && <LastRun run={run} now={now} />}
      </div>
    );
  }

  return (
    <SectionPanel title="Next cleanup" note={`every night at ${at} UTC`}>
      <div className="px-card py-4">
        {body}
        <Mark className="mt-3">
          Nothing still being delivered and nothing bookmarked is ever removed.
        </Mark>
      </div>
    </SectionPanel>
  );
};
